import { Component, OnInit, OnDestroy, HostListener, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { ShareService } from '@core/services/share-data/share-data.service';
import { VIEW_TYPES, STORAGE_NAMES, CONFIG, BREAKPOINTS, IS_PROD } from '@root/app.config';
import { QuizModalComponent } from '@shared/modals/quiz-modal/quiz-modal.component';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { PassDataService } from '@core/services/pass-data/pass-data.service';
import { ModalService } from '@core/services/modal/modal.service';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { BlindData, Breakpoints, QuizCloseButton } from '@root/app.interfaces';
import { FeedbackFormComponent } from '@shared/feedback-form/feedback-form.component';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { SetupPageService } from '@core/services/setup-page/setup-page.service';
import { GtmService } from '@core/services/gtm/gtm.service';
import { GTM_EVENTS } from '@core/services/gtm/gtm.events';
import { VariantService } from '@core/services/variant/variant.service';

@Component({
	selector: 'app-main-page',
	templateUrl: './main-page.component.html',
	styleUrls: ['./main-page.component.scss', './main-page.responsive.scss'],
})
export class MainPageComponent implements OnInit, OnDestroy {
	@ViewChild('feedbackTooltip') feedbackTooltip: NgbPopover;

	protected readonly IS_PROD = IS_PROD;
	protected readonly IS_SHOP = this.variant.isShop();

	getViewType: Subscription;
	getQuizHandler: Subscription;
	getBlindEmpty: Subscription;
	getBlindsData: Subscription;
	getSessionBlindsData: Subscription;
	getTaskbarControls: Subscription;
	getFeedbackTooltipStatus: Subscription;
	getSelectOpeningMode: Subscription;

	isMobile: boolean = this.passDataService.isMobileOrTablet;
	isLargeTablet: boolean = this.setupPageService.largeTabletCheck();
	isImageVisualisation = false;
	isSampleProject = false;
	quizShowed = false;
	hasBlinds: boolean;
	hiddenControl: boolean;
	showSelectOpeningScreen = false;
	breakpoints: Breakpoints = BREAKPOINTS;

	@HostListener('window:resize', ['$event'])
	onResize() {
		document.documentElement.style.height = `initial`;

		this.isLargeTablet = this.setupPageService.largeTabletCheck();
		this.openFeedbackTooltip();

		setTimeout(() => {
			document.documentElement.style.height = `100%`;

			setTimeout(() => {
				window.scrollTo(0, 1);
			}, 500);
		}, 500);
	}

	constructor(
		private gtm: GtmService,
		private matDialog: MatDialog,
		private variant: VariantService,
		private modalService: ModalService,
		private shareService: ShareService,
		public passDataService: PassDataService,
		private setupPageService: SetupPageService,
		private sessionStorageService: SessionStorageService,
	) {}

	ngOnInit(): void {
		this.taskbarHandler();
		this.viewTypeHandler();
		this.quizStatusHandler();
		this.blindsDataHandler();
		this.getFeedbackStatus();
		this.feedbackTooltipHandler();
		this.selectOpeningModeHandler();
	}

	ngOnDestroy(): void {
		this.getViewType.unsubscribe();
		this.getQuizHandler.unsubscribe();
		this.getBlindEmpty.unsubscribe();
		this.getBlindsData.unsubscribe();
		this.getSessionBlindsData.unsubscribe();
		this.getTaskbarControls.unsubscribe();
		this.getFeedbackTooltipStatus.unsubscribe();
		this.getSelectOpeningMode.unsubscribe();
	}

	taskbarHandler(): void {
		this.getTaskbarControls = this.shareService.getTaskbarControls.subscribe((res) => {
			const isLargeScreen = window.innerWidth > CONFIG.breakpoints['tablet-landscape'];

			if (!this.showSelectOpeningScreen) {
				this.hiddenControl = isLargeScreen ? res : false;
			}
		});
	}

	viewTypeHandler(): void {
		this.getViewType = this.shareService.getViewType.subscribe((type) => {
			const currentBackgroundImage = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_image_visualisation_background);
			this.isSampleProject =
				currentBackgroundImage?.type === 'outdoor' || this.sessionStorageService.getSession(STORAGE_NAMES.zip_country_code) === 'sg';
			this.isImageVisualisation = type === VIEW_TYPES.image_visualisation;
		});
	}

	onOpenQuizModal(): void {
		if (!this.matDialog.getDialogById(QuizModalComponent.ID)) {
			this.matDialog.open(QuizModalComponent, this.modalService.getConfig(QuizModalComponent.ID));
		}

		this.gtm.pushEvent(GTM_EVENTS.QUIZ_MODAL);
	}

	onOpenFeedbackModal(): void {
		this.closeFeedbackTooltip();
		this.matDialog.open(FeedbackFormComponent, this.modalService.getConfig(FeedbackFormComponent.ID));
		this.gtm.pushEvent(GTM_EVENTS.FEEDBACK_MODAL);
	}

	quizStatusHandler(): void {
		this.getQuizHandler = this.shareService.getQuizStatus.subscribe((data: QuizCloseButton) => (this.quizShowed = data.status));
	}

	blindsDataHandler(): void {
		const checkBlindsFromStorage = () => {
			const blindData: BlindData[] = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);

			this.hasBlinds = !!blindData?.length;
		};

		checkBlindsFromStorage();
		this.getBlindsData = this.shareService.currentData.subscribe((data: BlindData[]) => (this.hasBlinds = !!data.length));
		this.getBlindEmpty = this.shareService.getBlindEmpty.subscribe((status) => (this.hasBlinds = !status));
		this.getSessionBlindsData = this.shareService.getSessionBlindsData.subscribe(checkBlindsFromStorage.bind(this));
	}

	getFeedbackStatus(): void {
		this.passDataService.isFeedbackStatus = this.sessionStorageService.getSession(STORAGE_NAMES.zip_feedback_status);
	}

	feedbackTooltipHandler(): void {
		this.openFeedbackTooltip();

		this.getFeedbackTooltipStatus = this.shareService.getFeedbackTooltipStatus.subscribe((status: boolean) => {
			this.passDataService.isFeedbackTooltipStatus = status;
			this.openFeedbackTooltip();
		});
	}

	openFeedbackTooltip(): void {
		const popoverComponentStatus = this.feedbackTooltip && this.feedbackTooltip.ngbPopover && !this.feedbackTooltip.isOpen();

		if (this.passDataService.isFeedbackTooltipStatus && popoverComponentStatus) {
			this.feedbackTooltip.open();
		}
	}

	closeFeedbackTooltip(event: Event = null): void {
		event?.stopImmediatePropagation();

		if (this.feedbackTooltip && this.feedbackTooltip.ngbPopover) {
			this.passDataService.isFeedbackTooltipStatus = false;
			this.feedbackTooltip.close();
		}
	}

	selectOpeningModeHandler(): void {
		this.getSelectOpeningMode = this.shareService.getSelectOpeningMode.subscribe((enabled: boolean) => {
			this.hiddenControl = enabled;
			this.showSelectOpeningScreen = enabled;
		});
	}
}
